import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-light-svg-icons/faPencil';
import { faImage } from '@fortawesome/pro-light-svg-icons/faImage';
import { SerializedFile } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import { usePostFileMutation } from '../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole profile editor; each part uses its own slice of it */
import styles from './SMLProfile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileAvatarFieldProps {
  accountId: number;
  avatar?: SerializedFile;
  onChange: (avatar: SerializedFile | undefined) => void;
  disabled?: boolean;
}

/**
 * The avatar at the size and shape it renders on the page.
 *
 * The old screen offered a 150px dashed drop box, which told the artist
 * nothing about the circle their picture ends up in. The upload is the same
 * endpoint the rest of the app uses; only the frame around it changed.
 */
const ProfileAvatarFieldComponent: React.FC<ProfileAvatarFieldProps> = ({ accountId, avatar, onChange, disabled }) => {
  const intl = useIntl();
  const [postImage, { isLoading }] = usePostFileMutation();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file === undefined) {
        return;
      }
      const formData = new FormData();
      formData.append('file', file, file.name);
      postImage({ accountId, formData })
        .then((result) => {
          if (isSuccessfulResult(result)) {
            onChange(result.data);
          }
        })
        .catch(console.error);
    },
    [accountId, onChange, postImage]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpeg', '.jpg'] },
    disabled: disabled === true || isLoading,
    noClick: true,
    noKeyboard: true,
  });

  const previewUrl = useMemo(() => {
    if (avatar === undefined || avatar.url === '') {
      return undefined;
    }
    return getFullAPIFileURL(avatar.url);
  }, [avatar]);

  const remove = useCallback(() => onChange(undefined), [onChange]);

  const replaceLabel = intl.formatMessage({ id: 'app.sml.profile.avatar.replace', defaultMessage: 'Replace' });

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading -- react-dropzone hands its listeners over as a props bag
    <div className={styles.identityRow} {...getRootProps()}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- same props bag, for the hidden file input */}
      <input {...getInputProps()} />
      <div className={styles.avatar}>
        {previewUrl === undefined ? (
          <span className={styles.avatarEmpty}>
            <FontAwesomeIcon icon={faImage} />
          </span>
        ) : (
          <img className={styles.avatarImage} src={previewUrl} alt="" />
        )}
        <button type="button" className={styles.avatarEdit} onClick={open} aria-label={replaceLabel}>
          <FontAwesomeIcon icon={faPencil} />
        </button>
      </div>
      <div className={styles.identityText}>
        <span className={styles.fieldLabel}>
          {intl.formatMessage({ id: 'app.sml.profile.avatar.label', defaultMessage: 'Avatar' })}
        </span>
        <span className={styles.identityHelp}>
          {intl.formatMessage({
            id: 'app.sml.profile.avatar.help',
            defaultMessage: 'A square image, at least 400×400. It is shown as a circle at the top of your bio link.',
          })}
        </span>
      </div>
      <div className={styles.identityActions}>
        <SMLButton onClick={open} disabled={disabled === true || isLoading}>
          {isLoading
            ? intl.formatMessage({ id: 'app.sml.profile.avatar.uploading', defaultMessage: 'Uploading…' })
            : replaceLabel}
        </SMLButton>
        {avatar !== undefined && (
          <SMLButton onClick={remove} disabled={disabled}>
            {intl.formatMessage({ id: 'app.sml.profile.avatar.remove', defaultMessage: 'Remove' })}
          </SMLButton>
        )}
      </div>
    </div>
  );
};

export const ProfileAvatarField = TypedMemo(ProfileAvatarFieldComponent);
